/* eslint-disable no-prototype-builtins */
import jwtDecode from 'jwt-decode';
import { logoutUser } from 'actionCreators';
import { createConfirmation } from 'react-confirm';
import ConfirmDialog from 'components/ConfirmDialog';
import { startCase } from 'lodash';
import { LRRP_TEST_WORKROOMS,
         DELIVERY_SURFACE_TYPES_BLACKLIST,
         LHI_SOURCES,
         NON_LHI_SOURCES,
         VENDOR_NAME_MAP } from 'config/constants';

export const getIdFromPayload = payload => (
  payload.id !== null && payload.id !== undefined ? payload.id : payload.result
);

export const getResource = (entity, payload, id) => (
  payload.entities && payload.entities[entity] && payload.entities[entity][id]
);

export const createConstants = (...constants) => constants.reduce((acc, constant) => {
  const thing = acc;
  thing[constant] = constant;
  return thing;
}, {});

/*
  Adds an apiError property to the action meta field.
  It supports both plain objects & action creators
 */
export const wrapsWithApiError = (target, error) => {
  if (typeof target === 'object') return { ...target, meta: { ...target.meta, apiError: error } };
  return (...args) => {
    const action = target(...args);
    action.meta = Object.assign({}, action.meta, { apiError: error });
    return action;
  };
};

export const defaultApiError = error => wrapsWithApiError({ type: 'DEFAULT_API_ERROR' }, error);

export const listContainsElement = (list, element) => list.indexOf(element) > -1;

export const getUserData = (idToken) => {
  const payload = jwtDecode(idToken);
  return { uid: payload.sub, email: payload.email, image_url: payload.picture };
};

export const rejectAction = (action, reason = '') => action && Promise.reject(reason);

export const requireAllFields = (data, fields,
  errorMsg = 'All fields must be completed before saving',
  globalErrorKey = 'global',
  debug = false,
) => {
  let retValue = {};
  fields.forEach((key) => {
    if (!data.hasOwnProperty(key) || data[key] == null) {
      if (debug) console.debug('Field ', key, ' not present in ', data); // eslint-disable-line no-console
      retValue = { [globalErrorKey]: [errorMsg] };
    }
  });
  return retValue;
};

export const isEmpty = obj => Object.keys(obj).length === 0;

export const isImage = (file) => {
  if (!file || typeof file !== 'string') return false;
  const exts = ['jpg', 'jpeg', 'png', 'gif'];
  const lower = file.toLowerCase();
  return exts.some(ext => lower.indexOf(ext) > -1);
};

export function checkSessionExpired(dispatch, error) {
  if (error.response && error.response.status === 419) {
    dispatch(logoutUser('Your session has expired. You have been logged out due to inactivity.'));
  }
}

export const titleize = string => startCase(string);

export const normalize = collection => collection.reduce((acc, item) => {
  const thing = acc;
  thing[item.id] = item;
  return thing;
}, {});

export const formatNotes = notes => notes.replace(/\n\s*\n/g, ' | ');

export const mapObjectToList = data => Object.keys(data).map(key => data[key]);

export const toQueryString = obj => Object.keys(obj)
  .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`)
  .join('&');

export const fullAddress = ({ street_address = '', address = '', city = '', state = '', zip_code = '' }) => [
  address || street_address, city, state, zip_code].filter(Boolean).join(', ');

export const formatTelephone = value => (value ?
  value.split('').filter(c => c.match(/\d/)).map((n, index) => {
    if (index === 3 || index === 6) return `-${n}`;
    if (index === 10) return ` ext ${n}`;
    return n;
  }).join('') : '');

export const stringifyList = (list, selector = v => v.name) => list.map(item => selector(item)).join(', ');

export const error = (key, errors) => (errors && errors[key] && errors[key].length > 0 ? errors[key].join(', ') : null);

export const errorSummary = errorObj => (Array.isArray(errorObj) ? errorObj.map(err => `${err}`) :
  Object.keys(errorObj).map(key =>
    (Array.isArray(errorObj[key]) ?
      errorObj[key].map(err => `${titleize(key)} ${err}`).join(', ') : errorObj[key]),
  ).join(', '));

export const hasRequiredFields = (fields, object) =>
  fields.every(key => object.hasOwnProperty(key));

export const generateSource = (array, selector = v => v.name) =>
  array.map(item => ({ value: item.id, label: selector(item) }));

export const generateStringsSource = array => array.map(item => ({ value: item, label: item }));

export const nestedError = (property, index, name, errors) => error(`${property}[${index}].${name}`, errors);

export const removeNestedError = (entity, index, errors) => {
  const result = {};
  for (let i = 0; i < errors.keys; i += 1) {
    const anError = error[i];
    // if nested error not found, add it to result
    const errorName = entity.concat('[').concat(index).concat(']');
    if (anError.indexOf(errorName) === -1) result[anError] = errors[anError];
  }
  return result;
};

export const filterTypes = (accepted, hash) =>
  Object.fromEntries(Object.entries(hash).filter(([key]) => key.includes(accepted)));

const defaultConfirmation = createConfirmation(ConfirmDialog);

export const confirm = (confirmation, options = {}) =>
  defaultConfirmation({ confirmation, ...options });

export const filterByDivisionId = divisionId => region =>
  parseInt(divisionId, 10) === region.division_id;

export const filterByRegionId = regionId => market => parseInt(regionId, 10) === market.region_id;

export const phoneValue = (number, type) => (number ? `${number} (${type})` : '');

export const smoothScrollBy = ({ pixels = 0, percent = 0 }) => setTimeout(() => {
  const body = document.body;
  const html = document.documentElement;
  const height = Math.max(body.scrollHeight, body.offsetHeight,
    html.clientHeight, html.scrollHeight, html.offsetHeight);
  let amount;
  if (percent) amount = height * (percent / 100);
  else if (pixels) amount = pixels;
  else amount = 0;
  window.scrollBy({ left: 0, top: amount, behavior: 'smooth' });
}, 300);

export const isApexWorkroom = name => LRRP_TEST_WORKROOMS.includes(name);

export const roleNotInstallerBased = (role, allowInstaller = false) =>
  (allowInstaller ? role !== 'crew' : !['installer', 'crew'].includes(role));

export const getRandomStr = (length = 10) => {
  let result = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i += 1) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

export const isDeliveryPhase = (
  phaseType, apptSurfaceType, storeDeliveryMethod, spigDelivery,
) => phaseType === 'Installation' && !DELIVERY_SURFACE_TYPES_BLACKLIST.includes(apptSurfaceType) &&
     storeDeliveryMethod !== 'None' && spigDelivery;

export const validateSource = (source, arr = LHI_SOURCES) => arr.includes(source);

export const isSpig = source => source === 'SPIG';

export const vendorDisplayName = (source, workroomName, directKey = false) => {
  if (directKey) return isSpig(source) ? source : VENDOR_NAME_MAP[source];
  if (workroomName && workroomName.includes('PFT-')) return VENDOR_NAME_MAP.PFT;
  if (validateSource(source, NON_LHI_SOURCES)) return VENDOR_NAME_MAP[source];
  return VENDOR_NAME_MAP.LHI;
};

// Until we make PFT its own vendor, we have to make special consideration
// TODO: Remove this function when PFT is a vendor
export const deriveVendorKey = (phaseSource, optionSource, workroomName) => {
  if (optionSource === 'PFT' && (workroomName.includes('PFT-') || phaseSource === 'RF')) return 'PFT';
  if (validateSource(phaseSource)) return 'LHI';
  return phaseSource || '';
};

// Legacy behavior (reference only): options were derived from ALL_SOURCES,
// normalized to vendor-generated labels, filtered by phase vendor (with SPIG/PFT
// exceptions), deduped, then mapped to the same internal/external display labels.
export const woSourceOptions = () => ([
  { value: 'SPIG generated', label: 'Internally Generated' },
  { value: 'Lowe\'s generated', label: 'Externally Generated' },
]);

export const validStatus = (status, arr = []) => arr.includes(status.toLowerCase());

export const deriveStatusName = (status, options = { source: '', workroom: '' }) => {
  const { source, workroom } = options;
  return (source && validStatus(status.toLowerCase(), ['provider hold', 'cpo', 'hold'])) ?
    `Issue - Waiting on ${vendorDisplayName(source, workroom)}` : titleize(status) || 'N/A';
};

export const replaceFalsyObjVals = (obj, defaultVal = 'N/A') =>
  Object.fromEntries(Object.entries(obj).map(([key, value]) =>
    [key, !value ? defaultVal : value]));

export const normalizeSource = source => (validateSource(source) ? 'LHI' : source);



// WEBPACK FOOTER //
// ./client/app/utils/index.js